import React, { useRef, useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import type { Feature, Geometry } from "geojson";
import type { Layer, PathOptions, LatLngBounds } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/Button/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Info } from "lucide-react";
import { ChipMultiSelect } from "@/components/ChipMultiSelect/ChipMultiSelect";
import TotalAddressableMarketDrawer from "@/modules/stochastic/features/TotalAddressableMarket/components/TotalAddressableMarketDrawer/TotalAddressableMarketDrawer";
import { RemovableBadge } from "@/components/RemovableBadge/RemovableBadge";

import {
  TradeType,
  TRADE_OPTIONS,
} from "../../api/fetchZipcodeMarketValues/types";
import { useTotalAddressableMarket } from "../../hooks/useTotalAddressableMarket";
import { ColorMapType } from "../../slices/totalAddressableMarketSlice";

interface ZipcodeFeatureProperties {
  zipcode: string;
  market_value_electrical: number | null;
  market_value_plumbing: number | null;
  market_value_hvac: number | null;
  market_value_roofing: number | null;
}

const formatCurrency = (value: number | null): string => {
  if (value === null) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const interpolateColor = (
  color1: number[],
  color2: number[],
  factor: number,
): number[] => {
  return color1.map((c, i) => Math.round(c + factor * (color2[i] - c)));
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
};

const COLOR_MAPS: Record<ColorMapType, number[][]> = {
  viridis: [
    [68, 1, 84],
    [71, 19, 101],
    [72, 36, 117],
    [70, 52, 128],
    [65, 68, 135],
    [59, 82, 139],
    [53, 95, 141],
    [47, 108, 142],
    [42, 120, 142],
    [37, 132, 142],
    [33, 145, 140],
    [30, 156, 137],
    [34, 168, 132],
    [47, 180, 124],
    [68, 191, 112],
    [94, 201, 98],
    [122, 209, 81],
    [155, 217, 60],
    [189, 223, 38],
    [253, 231, 37],
  ],
  magma: [
    [0, 0, 4],
    [11, 7, 36],
    [27, 12, 65],
    [47, 15, 95],
    [70, 16, 118],
    [94, 17, 127],
    [117, 30, 129],
    [140, 41, 129],
    [163, 51, 126],
    [186, 61, 120],
    [208, 72, 109],
    [228, 88, 98],
    [243, 109, 93],
    [251, 133, 97],
    [254, 158, 111],
    [254, 181, 131],
    [254, 204, 153],
    [254, 224, 176],
    [253, 241, 199],
    [252, 253, 191],
  ],
  inferno: [
    [0, 0, 4],
    [12, 7, 39],
    [27, 12, 70],
    [46, 12, 98],
    [70, 13, 114],
    [94, 19, 117],
    [117, 28, 112],
    [140, 39, 105],
    [161, 51, 94],
    [182, 63, 81],
    [201, 78, 66],
    [219, 94, 49],
    [234, 113, 32],
    [246, 135, 14],
    [252, 159, 9],
    [252, 183, 25],
    [248, 207, 52],
    [241, 229, 85],
    [244, 246, 124],
    [252, 255, 164],
  ],
  turbo: [
    [48, 18, 59],
    [56, 49, 132],
    [61, 78, 178],
    [56, 107, 212],
    [43, 135, 234],
    [30, 162, 240],
    [24, 188, 226],
    [29, 211, 194],
    [48, 230, 154],
    [79, 243, 113],
    [119, 249, 78],
    [160, 252, 60],
    [197, 247, 57],
    [228, 234, 54],
    [248, 213, 49],
    [254, 184, 45],
    [252, 152, 42],
    [246, 118, 39],
    [234, 82, 35],
    [190, 30, 45],
  ],
};

const COLORMAP_OPTIONS: { value: ColorMapType; label: string }[] = [
  { value: "viridis", label: "Viridis" },
  { value: "magma", label: "Magma" },
  { value: "inferno", label: "Inferno" },
  { value: "turbo", label: "Turbo" },
];

const getColor = (
  value: number | null,
  min: number,
  max: number,
  colorMap: ColorMapType,
): string => {
  if (value === null) return "#cccccc";
  const stops = COLOR_MAPS[colorMap];
  if (min === max) {
    const mid = stops[Math.floor(stops.length / 2)];
    return rgbToHex(mid[0], mid[1], mid[2]);
  }

  const normalized = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const numStops = stops.length;
  const scaledPosition = normalized * (numStops - 1);
  const lowerIndex = Math.floor(scaledPosition);
  const upperIndex = Math.min(lowerIndex + 1, numStops - 1);
  const factor = scaledPosition - lowerIndex;

  const interpolated = interpolateColor(
    stops[lowerIndex],
    stops[upperIndex],
    factor,
  );
  return rgbToHex(interpolated[0], interpolated[1], interpolated[2]);
};

const getGradientCSS = (colorMap: ColorMapType): string => {
  const stops = COLOR_MAPS[colorMap];
  const colors = [...stops].reverse().map((c) => rgbToHex(c[0], c[1], c[2]));
  return `linear-gradient(to bottom, ${colors.join(", ")})`;
};

interface FitBoundsProps {
  bounds: LatLngBounds | null;
}

const FitBounds: React.FC<FitBoundsProps> = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);

  return null;
};

const TotalAddressableMarketPage: React.FC = () => {
  const [bounds, setBounds] = useState<LatLngBounds | null>(null);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const geoJsonRef = useRef<L.GeoJSON | null>(null);

  const {
    marketData,
    selectedTrade,
    selectedColorMap,
    selectedLocations,
    loading,
    error,
    dataVersion,
    geoJsonData,
    marketValueKey,
    minValue,
    maxValue,
    zipCodeInput,
    zipCodeList,
    zipCodeError,
    hasZipCodes,
    setZipCodeInput,
    handleAddZipCode,
    handleRemoveZipCode,
    handleClear,
    handleKeyDown,
    handleTradeChange,
    handleColorMapChange,
    handleSubmit,
    handleLocationChange,
    fetchLocationsForPicker,
  } = useTotalAddressableMarket();

  const styleFeature = useCallback(
    (feature?: Feature): PathOptions => {
      if (!feature?.properties) {
        return {
          fillColor: "#cccccc",
          weight: 2,
          opacity: 1,
          color: "white",
          fillOpacity: 0.7,
        };
      }
      const value = feature.properties[marketValueKey] as number | null;
      return {
        fillColor: getColor(value, minValue, maxValue, selectedColorMap),
        weight: 2,
        opacity: 1,
        color: "white",
        fillOpacity: 0.7,
      };
    },
    [marketValueKey, minValue, maxValue, selectedColorMap],
  );

  const onEachFeature = useCallback(
    (feature: Feature<Geometry, ZipcodeFeatureProperties>, layer: Layer) => {
      const props = feature.properties;
      if (!props) return;

      const tooltipContent = `
        <div class="p-2 min-w-[180px]">
          <div class="font-bold text-base mb-2 border-b pb-1">ZIP: ${props.zipcode}</div>
          <div class="space-y-1 text-sm">
            <div class="flex justify-between"><span>HVAC:</span><span class="font-medium">${formatCurrency(props.market_value_hvac)}</span></div>
            <div class="flex justify-between"><span>Electrical:</span><span class="font-medium">${formatCurrency(props.market_value_electrical)}</span></div>
            <div class="flex justify-between"><span>Plumbing:</span><span class="font-medium">${formatCurrency(props.market_value_plumbing)}</span></div>
            <div class="flex justify-between"><span>Roofing:</span><span class="font-medium">${formatCurrency(props.market_value_roofing)}</span></div>
          </div>
        </div>
      `;

      layer.bindTooltip(tooltipContent, {
        sticky: true,
        direction: "auto",
        className: "leaflet-tooltip-custom",
      });

      layer.on({
        mouseover: (e) => {
          const targetLayer = e.target;
          targetLayer.setStyle({
            weight: 3,
            color: "#333",
            fillOpacity: 0.9,
          });
          targetLayer.bringToFront();
        },
        mouseout: (e) => {
          if (geoJsonRef.current) {
            geoJsonRef.current.resetStyle(e.target);
          }
        },
      });
    },
    [],
  );

  useEffect(() => {
    if (geoJsonRef.current && geoJsonData.features.length > 0) {
      const layerBounds = geoJsonRef.current.getBounds();
      if (layerBounds.isValid()) {
        const paddedBounds = layerBounds.pad(0.1);
        setBounds(paddedBounds);
      }
    }
  }, [dataVersion, geoJsonData.features.length]);

  return (
    <MainPageWrapper title="Total Addressable Market">
      <style>{`
        .leaflet-tooltip-custom {
          background: white;
          border: 1px solid #ccc;
          border-radius: 8px;
          padding: 0;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        .leaflet-tooltip-custom::before {
          display: none;
        }
      `}</style>
      <div className="min-h-screen transition-colors duration-200 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              ZIP Code Search
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
              <div className="w-full sm:w-auto">
                <Label className="mb-2 block">Location</Label>
                <ChipMultiSelect
                  entityNamePlural="Locations"
                  entityNameSingular="Location"
                  fetchEntities={fetchLocationsForPicker}
                  label="Select Locations"
                  onChange={handleLocationChange}
                  value={selectedLocations}
                />
              </div>
              <div className="w-full sm:flex-1">
                <Label className="mb-2 block" htmlFor="zipCodes">
                  ZIP Codes
                </Label>
                <div className="flex gap-2">
                  <Input
                    className="flex-1 h-9 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-medium px-3 text-gray-600 dark:text-gray-300 placeholder:font-medium shadow-sm hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                    id="zipCodes"
                    onChange={(e) => setZipCodeInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter ZIP Code(s), separated by commas"
                    type="text"
                    value={zipCodeInput}
                  />
                  <Button
                    className="h-9 rounded-md px-3 text-sm font-medium shadow-sm"
                    onClick={handleAddZipCode}
                    type="button"
                  >
                    Add
                  </Button>
                </div>
              </div>
              <div className="w-full sm:w-auto">
                <Label className="mb-2 block" htmlFor="trade">
                  Color by Trade
                </Label>
                <Select
                  onValueChange={(value) =>
                    handleTradeChange(value as TradeType)
                  }
                  value={selectedTrade}
                >
                  <SelectTrigger
                    className="h-9 w-full sm:w-[130px] rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-medium px-3 text-gray-600 dark:text-gray-300 shadow-sm hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                    id="trade"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[1000]">
                    {TRADE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-auto">
                <Label className="mb-2 block" htmlFor="colormap">
                  Color Map
                </Label>
                <Select
                  onValueChange={(value) =>
                    handleColorMapChange(value as ColorMapType)
                  }
                  value={selectedColorMap}
                >
                  <SelectTrigger
                    className="h-9 w-full sm:w-[130px] rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-medium px-3 text-gray-600 dark:text-gray-300 shadow-sm hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                    id="colormap"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[1000]">
                    {COLORMAP_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-auto sm:self-end">
                <Button
                  className="h-9 w-full sm:w-auto rounded-md px-3 text-sm font-medium shadow-sm"
                  disabled={loading || !hasZipCodes}
                  onClick={handleSubmit}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </div>
            </div>

            {zipCodeError && (
              <p className="text-red-500 mt-2 text-sm">{zipCodeError}</p>
            )}
            {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}

            {hasZipCodes && (
              <div className="pt-4 mt-4 border-t border-gray-100 dark:border-slate-700">
                <span className="text-sm text-gray-500 dark:text-gray-400 block mb-2">
                  {zipCodeList.length} ZIP Code
                  {zipCodeList.length !== 1 ? "s" : ""} Selected:
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  {zipCodeList.map((zipCode) => (
                    <RemovableBadge
                      color="red"
                      key={zipCode}
                      label={zipCode}
                      onRemove={() => handleRemoveZipCode(zipCode)}
                    />
                  ))}
                  <button
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    onClick={handleClear}
                    type="button"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Market Value Map
              </h2>
              <div className="flex items-center gap-4">
                <Button
                  className="h-8 rounded-md px-3 text-xs font-semibold text-primary border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700"
                  onClick={() => setExplainerOpen(true)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <Info className="h-3.5 w-3.5 mr-1.5" />
                  How is TAM calculated?
                </Button>
                {marketData.length > 0 && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {marketData.length} ZIP Code
                    {marketData.length !== 1 ? "s" : ""} Loaded
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-1 h-[550px] rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700 relative z-0">
                <MapContainer
                  center={[39.8283, -98.5795]}
                  scrollWheelZoom={true}
                  style={{ height: "100%", width: "100%" }}
                  zoom={4}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {geoJsonData.features.length > 0 && (
                    <GeoJSON
                      data={geoJsonData}
                      key={`${selectedTrade}-${selectedColorMap}-${dataVersion}`}
                      onEachFeature={onEachFeature}
                      ref={geoJsonRef}
                      style={styleFeature}
                    />
                  )}
                  <FitBounds bounds={bounds} />
                </MapContainer>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="text-sm font-medium mb-2 text-gray-900 dark:text-white">
                  {formatCurrency(maxValue)}
                </div>
                <div
                  className="w-8 rounded"
                  style={{
                    height: "300px",
                    background: getGradientCSS(selectedColorMap),
                  }}
                />
                <div className="text-sm font-medium mt-2 text-gray-900 dark:text-white">
                  {formatCurrency(minValue)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  {TRADE_OPTIONS.find((o) => o.value === selectedTrade)?.label}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TAM Explainer Drawer */}
      <TotalAddressableMarketDrawer
        isOpen={explainerOpen}
        onClose={() => setExplainerOpen(false)}
      />
    </MainPageWrapper>
  );
};

export default TotalAddressableMarketPage;
